// The NAV-BACK store — what powers Otto's "← Back to <where you were>" affordance. When Otto TAKES the user
// to another page (a nav card's "Go to …", a Q&A deep link or suggestion chip, a cross-capability "open the …
// page" pick, or a plan's navigate step) the origin is gone: the route unmounts Otto and the destination
// mounts a fresh one, so nothing in React state survives to say where they came from. This module snapshots
// that origin SYNCHRONOUSLY into localStorage at navigate time — the same reason pendingPlanStore exists (a
// React-state write plus the chat's persist effect would be dropped by the unmount happening in the same tick).
//
// The snapshot IS a navigation TARGET ({ url, state, bc }), so going back reuses the ordinary nav executor
// verbatim (dirty-form guard, FCA notice, breadcrumbs) — there's no second navigation path to keep correct.
// We push an explicit target rather than calling history.goBack() for two reasons: "back" must always mean
// "the page Otto took you FROM" (not wherever the user wandered on the destination), and the breadcrumb trail
// they actually had is restored instead of being left as the destination's.
//
// Model mirrors the filter Undo: a newest-first STACK (only the top hop is offered), consumed when used,
// TTL'd so a stale hop can never offer a surprise jump on a later visit, and wiped by the chat's Clear.
const KEY = "otto.navBack.v1";
export const NAV_BACK_TTL_MS = 60 * 60 * 1000; // 1 hour — long enough for a real session, short enough to go stale
const MAX_ENTRIES = 5;

// Breadcrumbs live in sessionStorage under "bc-data" — the contract owned by config/utils
// (bulkResetBreadCrumpData writes it, the breadcrumb bar reads it on the "bc-data-changed" event). We read
// the trail here and hand it back as a target's `bc`, so restoring is that same one function.
const BC_KEY = "bc-data";

const read = () => {
    try {
        const raw = typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem(KEY) : null;
        const parsed = raw ? JSON.parse(raw) : null;
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return []; // corrupt/unreadable — degrade to "no back available", never throw into a click handler
    }
};

const write = list => {
    try {
        window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
        /* quota/blocked — the affordance degrades to not being offered */
    }
};

export const clearNavBack = () => {
    try {
        window.localStorage.removeItem(KEY);
    } catch (e) {
        /* ignore */
    }
};

const isFresh = e => !!(e && e.url && typeof e.at === "number" && Date.now() - e.at <= NAV_BACK_TTL_MS);

// The live stack, oldest -> newest, with expired/malformed entries dropped (and the pruning persisted, so a
// stale entry self-cleans on the first read rather than lingering).
const liveEntries = () => {
    const all = read();
    const live = all.filter(isFresh);
    if (live.length !== all.length) {
        if (live.length) write(live);
        else clearNavBack();
    }
    return live;
};

// The app's current location as the same "path + search + hash" string a nav target carries, so an origin
// snapshot can be compared against where the user is standing. Reads the router's history when available
// (its `location.state` is the state a push would restore) and falls back to window. Requires config/history
// LAZILY — like navExecutor, so merely importing this module pulls no router/store graph into a unit test.
const currentLocation = () => {
    try {
        // eslint-disable-next-line global-require
        const history = require("../../../../config/history").default;
        if (history && history.location) return history.location;
    } catch (e) {
        /* fall through to window */
    }
    try {
        if (typeof window !== "undefined" && window.location) {
            const entry = window.history && window.history.state;
            return {
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                // history v4 stores a push's state under window.history.state.state
                state: entry && entry.state
            };
        }
    } catch (e) {
        /* no location available */
    }
    return null;
};

const toUrl = loc => (loc ? `${loc.pathname || ""}${loc.search || ""}${loc.hash || ""}` : "");

export const currentUrl = () => toUrl(currentLocation());

// Is the user standing on `url` right now? Used to hide a hop that can't move anyone — the push was
// cancelled (a dirty-form prompt deferred it) or they already got back another way.
export const isCurrentLocation = url => !!url && currentUrl() === url;

// Snapshot the page being LEFT as a nav-back entry. `label` is how Otto names that page in prose
// ("FCA Dashboard" / "Recommendations Table View"); `msgIdx` anchors the affordance under the chat card that
// navigated (null for a navigation with no card, e.g. a plan step); `target` records where the hop went and
// `trace` the turn's ids, both for the back span. Returns null when there's no readable location.
export const captureOrigin = ({ label, page, msgIdx, target, trace } = {}) => {
    const loc = currentLocation();
    const url = toUrl(loc);
    if (!url) return null;
    let bc = [];
    try {
        const raw = window.sessionStorage.getItem(BC_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (Array.isArray(parsed)) bc = parsed;
    } catch (e) {
        /* no readable trail — going back just leaves no crumbs, which is what an empty bc means */
    }
    return {
        url,
        state: loc && loc.state ? loc.state : undefined,
        bc,
        label: label || "",
        page: page || null,
        msgIdx: typeof msgIdx === "number" ? msgIdx : null,
        target: target || null,
        trace: trace || null,
        at: Date.now()
    };
};

// Push an origin snapshot onto the stack. Deduped by url (a repeat visit to the same origin moves it to the
// top rather than growing a cycle) and capped, so the chain stays short and acyclic.
export const pushNavBack = entry => {
    if (!entry || !entry.url) return null;
    const list = liveEntries().filter(e => e.url !== entry.url);
    list.push({ ...entry, at: typeof entry.at === "number" ? entry.at : Date.now() });
    write(list.slice(-MAX_ENTRIES));
    return entry;
};

// The hop currently offered (the newest), or null.
export const peekNavBack = () => {
    const live = liveEntries();
    return live.length ? live[live.length - 1] : null;
};

// Take the newest hop off the stack (it's being used) and return it; the one before it becomes the offered
// hop — newest-first, exactly like the filter undo stack.
export const popNavBack = () => {
    const live = liveEntries();
    const top = live.pop() || null;
    if (live.length) write(live);
    else clearNavBack();
    return top;
};
